import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import moment from 'moment';
import debounce from 'lodash/debounce';
import {withStyles, TextField, Button, CircularProgress} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {Alert} from '@material-ui/lab';
import {
    salesDemoList,
    salesDemoStart,
    salesDemoStatus,
    salesDemoSearch,
    clearSalesDemoSearch,
    clearSalesDemo,
} from '../../../../../redux/actions/General/SalesDemo/salesDemoActions';

const titleStyles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(titleStyles)(props => {
    const {children, classes, onClose, ...other} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button"/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

// Bir kosu 2-5 dakika suruyor; api 5 sn aralikla sorgulanmasini istiyor.
const POLL_INTERVAL_MS = 5000;
const ACTIVE_STATUSES = ['pending', 'running'];
const RECENT_RUN_LIMIT = 5;
// api otel adiyla aramayi en az 3 karakterle kabul ediyor
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

const RUN_STATUS_TEXT = {
    pending: 'Sırada',
    running: 'Hazırlanıyor',
    completed: 'Hazır',
    completed_with_errors: 'Hazır, bazı adımlar başarısız',
    failed: 'Başarısız',
};

const RUN_STATUS_SEVERITY = {
    pending: 'info',
    running: 'info',
    completed: 'success',
    completed_with_errors: 'warning',
    failed: 'error',
};

const STEP_ICONS = {
    pending: 'far fa-circle',
    running: 'fas fa-spinner fa-spin',
    completed: 'fas fa-check-circle',
    failed: 'fas fa-times-circle',
    warning: 'fas fa-exclamation-circle',
    skipped: 'fas fa-minus-circle',
};

const STEP_COLORS = {
    pending: '#9aa3af',
    running: '#2f6fed',
    completed: '#2e9e5b',
    failed: '#d64545',
    warning: '#d97706',
    skipped: '#9aa3af',
};

// Adimlarin sirasi her zaman api'deki steps dizisinden gelir (SalesDemoService::STEPS).
// Haritada olmayan (sonradan eklenen) adim adi oldugu gibi gosterilir.
const STEP_LABELS = {
    validate: 'Kontrol',
    create_property: 'Tesis açılıyor',
    booking_import: 'Booking içeriği alınıyor',
    web_create: 'Web sitesi kuruluyor',
    site_brand: 'Site başlığı ayarlanıyor',
    property_city: 'Şehir bilgisi ekleniyor',
    site_google: 'Google bilgileri alınıyor',
    site_own_photos: 'Otelin sitesinden fotoğraflar alınıyor',
    site_rooms: 'Odalar siteye ekleniyor',
    booking_engine: 'Rezervasyon motoru kuruluyor',
    site_slider: 'Slider fotoğrafları seçiliyor',
    description_persist: 'Açıklama kaydediliyor',
    ai_content: 'AI metinleri yazılıyor',
    site_blog: 'Blog yazıları yazılıyor',
    site_menu: 'Site menüsü oluşturuluyor',
    seo_meta: 'SEO bilgileri hazırlanıyor',
    purge: 'Site önbelleği temizleniyor',
    verify: 'Site kontrol ediliyor',
};

const buttonStyle = {
    height: 40,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
};

const isDirectInput = (value) => /^\d+$/.test(value) || /^https?:\/\//i.test(value);

// Listeden secilen otel > yalniz rakam (Booking.com otel ID'si) > link. Link ise api icinden
// ID'yi cikariyor; ID tasimayan otel sayfasi linki api'den 400 doner.
const buildTarget = (value, selectedHotel) => {
    if (selectedHotel) {
        return {booking_hotel_id: String(selectedHotel.booking_hotel_id)};
    }
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
        return {booking_hotel_id: trimmed};
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return {booking_url: trimmed};
    }
    return null;
};

const createIdempotencyKey = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    // randomUUID olmayan tarayicilar icin RFC 4122 v4
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// expires_at unix saniye; created_at unix ya da tarih metni olabilir.
const formatTime = (value) => {
    if (!value) {
        return '';
    }
    const date = /^\d+$/.test(String(value)) ? moment.unix(Number(value)) : moment(value);
    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '';
};

const getErrorMessage = (error) =>
    (error && error.response && error.response.data && error.response.data.message)
    || (error && 'Bir hata oluştu. Lütfen tekrar deneyin.');

const getStepLabel = (step) => STEP_LABELS[step.step] || step.step;

const getRunTitle = (run) => run.hotel_name || `Booking.com ID ${run.booking_hotel_id}`;

const getHotelPlace = (hotel) => [hotel.city, hotel.country].filter(Boolean).join(', ');

function SalesDemoDialog({
    open,
    onClose,
    onGoToProperty,
    onRunFinished,
    runs,
    run,
    listFetching,
    startFetching,
    startError,
    statusError,
    searchQuery,
    searchFetching,
    searchResults,
    searchError,
    salesDemoList,
    salesDemoStart,
    salesDemoStatus,
    salesDemoSearch,
    clearSalesDemoSearch,
    clearSalesDemo,
}) {
    const [bookingInput, setBookingInput] = useState('');
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [domainInput, setDomainInput] = useState('');
    const [validationError, setValidationError] = useState('');
    const [copied, setCopied] = useState(false);
    const [listReady, setListReady] = useState(false);

    // Ayni gonderim tekrarlanirsa (ag hatasi, cift tik) ayni anahtar gitsin; api ayni kosuyu dondurur.
    const pendingKeyRef = useRef(null);
    const wasListFetchingRef = useRef(false);
    const lastSeenRef = useRef({id: null, status: null});
    const debouncedSearchRef = useRef(debounce(query => salesDemoSearch(query), SEARCH_DEBOUNCE_MS));

    useEffect(() => {
        const debouncedSearch = debouncedSearchRef.current;
        return () => debouncedSearch.cancel();
    }, []);

    const runId = run && run.run_id;
    const runStatus = run && run.status;
    const isActive = ACTIVE_STATUSES.includes(runStatus);

    useEffect(() => {
        if (open) {
            salesDemoList();
        } else {
            setListReady(false);
        }
    }, [open, salesDemoList]);

    useEffect(() => {
        if (open && wasListFetchingRef.current && !listFetching) {
            setListReady(true);
        }
        wasListFetchingRef.current = listFetching;
    }, [open, listFetching]);

    // Liste geldiginde suren bir kosu varsa ona devam et (baska cihazda baslatilmis olabilir)
    useEffect(() => {
        if (!listReady || run) {
            return;
        }
        const activeRun = runs.find(item => ACTIVE_STATUSES.includes(item.status));
        if (activeRun) {
            salesDemoStatus(activeRun.run_id);
        }
    }, [listReady]);

    // Her cevaptan sonra bir sonraki sorgu kurulur; dialog kapaninca sorgu durur, acilinca devam eder.
    useEffect(() => {
        if (!open || !runId || !isActive) {
            return undefined;
        }
        const timer = setTimeout(() => salesDemoStatus(runId), POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [open, runId, isActive, run, statusError, salesDemoStatus]);

    // Izledigimiz kosu bitince tesis listesi yenilensin: yeni tesis (status 8) listede gorunur.
    useEffect(() => {
        const last = lastSeenRef.current;
        if (last.id === runId && ACTIVE_STATUSES.includes(last.status)
            && runStatus && !ACTIVE_STATUSES.includes(runStatus)
            && typeof onRunFinished === 'function') {
            onRunFinished();
        }
        lastSeenRef.current = {id: runId, status: runStatus};
    }, [runId, runStatus, onRunFinished]);

    // Otel adi yazilirsa arama yapilir; rakam ya da link dogrudan kullanilir.
    const handleBookingInputChange = (value) => {
        setBookingInput(value);
        setSelectedHotel(null);
        setValidationError('');

        const trimmed = value.trim();
        if (trimmed.length >= SEARCH_MIN_LENGTH && !isDirectInput(trimmed)) {
            debouncedSearchRef.current(trimmed);
        } else {
            debouncedSearchRef.current.cancel();
            clearSalesDemoSearch();
        }
    };

    const handleSelectHotel = (hotel) => {
        debouncedSearchRef.current.cancel();
        setSelectedHotel(hotel);
        setBookingInput(hotel.name || String(hotel.booking_hotel_id));
        clearSalesDemoSearch();
    };

    const handleStart = () => {
        const target = buildTarget(bookingInput, selectedHotel);
        if (!target) {
            setValidationError("Listeden bir otel seçin ya da Booking.com otel ID'si (yalnız rakam) veya otel ID'sini içeren bir Booking.com linki giriniz.");
            return;
        }
        setValidationError('');

        const domain = domainInput.trim();
        const params = domain ? {...target, domain} : target;

        // Ayni hedef + alan adi tekrar gonderilirse ayni anahtar gider
        const input = JSON.stringify(params);
        if (!pendingKeyRef.current || pendingKeyRef.current.input !== input) {
            pendingKeyRef.current = {input, key: createIdempotencyKey()};
        }
        salesDemoStart({...params, idempotency_key: pendingKeyRef.current.key});
    };

    // Yeniden denemede yeni anahtar gonderilmez: yeni anahtar ikinci bir otel acar.
    const handleRetry = () => {
        if (runId) {
            salesDemoStart({run_id: runId, retry: true});
        }
    };

    const handleNewDemo = () => {
        clearSalesDemo();
        clearSalesDemoSearch();
        pendingKeyRef.current = null;
        setSelectedHotel(null);
        setDomainInput('');
        setBookingInput('');
        setValidationError('');
        setCopied(false);
    };

    const handleSelectRun = (item) => {
        setCopied(false);
        salesDemoStatus(item.run_id);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(run.preview_url);
        setCopied(true);

        setTimeout(() => {
            setCopied(false);
        }, 1000);
    };

    const handleOpenLink = () => {
        window.open(run.preview_url, '_blank', 'noopener,noreferrer');
    };

    const handleGoToProperty = () => {
        if (run && run.property_id && typeof onGoToProperty === 'function') {
            onGoToProperty(run.property_id);
        }
    };

    const steps = run && Array.isArray(run.steps) ? run.steps : [];
    const failedSteps = steps.filter(step => step.status === 'failed');
    const canRetry = runStatus === 'failed' || runStatus === 'completed_with_errors';
    const startErrorMessage = startError && getErrorMessage(startError);
    const recentRuns = (runs || []).slice(0, RECENT_RUN_LIMIT);
    const trimmedInput = bookingInput.trim();
    const showSearch = !run && !selectedHotel && trimmedInput.length >= SEARCH_MIN_LENGTH
        && !isDirectInput(trimmedInput);
    const searchSettled = showSearch && !searchFetching && searchQuery === trimmedInput;

    return (
        <Dialog
            onClose={onClose}
            open={open}
            maxWidth="sm"
            fullWidth
            disableBackdropClick={true}
            aria-labelledby="sales-demo-title"
        >
            <DialogTitle className="red-bottom-border" id="sales-demo-title" onClose={onClose}>
                Satış Demosu Oluştur
            </DialogTitle>
            <DialogContent>
                {!run && (
                    <>
                        <Typography variant="body2" style={{color: '#5b6675', marginBottom: 16}}>
                            Otel adını yazıp listeden seçin ya da Booking.com ID'sini veya ID'yi içeren
                            Booking.com linkini giriniz. Otel bilgileri, fotoğraflar ve odalar aktarılıp demo
                            site hazırlanır; işlem 2-5 dakika sürer. Pencereyi kapatsanız da işlem devam eder.
                        </Typography>

                        <div style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                            marginBottom: 16,
                        }}>
                            <div style={{flex: '1 1 240px', minWidth: 200}}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Otel adı, Booking.com ID veya link"
                                    placeholder="örn: Grand Ons ya da 1234567"
                                    value={bookingInput}
                                    onChange={e => handleBookingInputChange(e.target.value)}
                                    disabled={startFetching}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleStart();
                                        }
                                    }}
                                />
                            </div>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleStart}
                                disabled={startFetching || !bookingInput.trim()}
                                style={{...buttonStyle, minWidth: 160}}
                            >
                                {startFetching
                                    ? <CircularProgress size={20} color="inherit"/>
                                    : 'Demo Oluştur'}
                            </Button>
                        </div>

                        {showSearch && searchFetching && (
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, color: '#5b6675', marginBottom: 16}}>
                                <CircularProgress size={16}/> Booking.com'da aranıyor...
                            </div>
                        )}

                        {showSearch && searchSettled && searchError && (
                            <Alert severity="warning" style={{marginBottom: 16}}>
                                {getErrorMessage(searchError)}
                            </Alert>
                        )}

                        {showSearch && searchSettled && !searchError && searchResults.length === 0 && (
                            <Typography variant="body2" style={{color: '#5b6675', marginBottom: 16}}>
                                Sonuç bulunamadı. Booking.com ID'si ya da linki girebilirsiniz.
                            </Typography>
                        )}

                        {showSearch && searchSettled && searchResults.length > 0 && (
                            <div style={{border: '1px solid #eef0f4', borderRadius: 6, marginBottom: 16, maxHeight: 320, overflowY: 'auto'}}>
                                {searchResults.map(hotel => (
                                    <div
                                        key={hotel.booking_hotel_id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleSelectHotel(hotel)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                handleSelectHotel(hotel);
                                            }
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: 8,
                                            borderBottom: '1px solid #eef0f4',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {hotel.photo_url
                                            ? <img src={hotel.photo_url} alt="" style={{width: 48, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0}}/>
                                            : <div style={{width: 48, height: 48, borderRadius: 4, background: '#ccced5', flexShrink: 0}}/>}
                                        <div style={{minWidth: 0}}>
                                            <div style={{fontWeight: 600, color: '#1f2937'}}>{hotel.name}</div>
                                            <div style={{fontSize: 12, color: '#5b6675'}}>
                                                {getHotelPlace(hotel)}
                                                {getHotelPlace(hotel) && ' · '}
                                                Booking.com ID {hotel.booking_hotel_id}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedHotel && (
                            <Alert severity="info" style={{marginBottom: 16}}>
                                Seçilen otel: <strong>{selectedHotel.name}</strong>
                                {getHotelPlace(selectedHotel) && ` — ${getHotelPlace(selectedHotel)}`}
                                {' '}(Booking.com ID {selectedHotel.booking_hotel_id})
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Otelin kendi alan adı (isteğe bağlı)"
                            placeholder="örn: otelim.com"
                            value={domainInput}
                            onChange={e => setDomainInput(e.target.value)}
                            disabled={startFetching}
                            style={{marginBottom: 16}}
                        />
                    </>
                )}

                {validationError && (
                    <Alert severity="error" style={{marginBottom: 16}}>{validationError}</Alert>
                )}

                {!validationError && startErrorMessage && (
                    <Alert severity="error" style={{marginBottom: 16}}>{startErrorMessage}</Alert>
                )}

                {run && (
                    <>
                        <Alert severity={RUN_STATUS_SEVERITY[runStatus] || 'info'} style={{marginBottom: 16}}>
                            <strong>{getRunTitle(run)}</strong>
                            {' — '}{RUN_STATUS_TEXT[runStatus] || runStatus}
                            {run.domain && <div>Alan adı: {run.domain}</div>}
                            {run.existing && (
                                <div>Bu otel için daha önce başlatılmış demo gösteriliyor.</div>
                            )}
                        </Alert>

                        {isActive && (
                            <Typography variant="body2" style={{color: '#5b6675', marginBottom: 16}}>
                                Demo hazırlanıyor (2-5 dk). Bitince size e-posta ile link gönderilecek;
                                bu pencereyi kapatabilirsiniz.
                            </Typography>
                        )}

                        {isActive && statusError && (
                            <Alert severity="warning" style={{marginBottom: 16}}>
                                Durum alınamadı, tekrar deneniyor.
                            </Alert>
                        )}

                        {steps.length > 0 && (
                            <div style={{marginBottom: 16}}>
                                {steps.map(step => (
                                    <div key={step.step} style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 10,
                                        padding: '6px 0',
                                        borderBottom: '1px solid #eef0f4',
                                    }}>
                                        <i
                                            className={STEP_ICONS[step.status] || STEP_ICONS.pending}
                                            style={{color: STEP_COLORS[step.status] || STEP_COLORS.pending, marginTop: 3, width: 16}}
                                        />
                                        <div style={{flex: 1}}>
                                            <div style={{fontWeight: 600, color: '#1f2937'}}>{getStepLabel(step)}</div>
                                            {step.message && (
                                                <div style={{fontSize: 12, color: '#5b6675'}}>{step.message}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {runStatus === 'completed_with_errors' && failedSteps.length > 0 && (
                            <Alert severity="warning" style={{marginBottom: 16}}>
                                Link kullanılabilir, ancak şu adımlar başarısız oldu:{' '}
                                <strong>{failedSteps.map(getStepLabel).join(', ')}</strong>
                            </Alert>
                        )}

                        {runStatus === 'failed' && (
                            <Alert severity="error" style={{marginBottom: 16}}>
                                Demo hazırlanamadı.
                                {failedSteps.length > 0 && (
                                    <>
                                        {' '}Başarısız adım:{' '}
                                        <strong>{failedSteps.map(getStepLabel).join(', ')}</strong>
                                    </>
                                )}
                            </Alert>
                        )}

                        {run.preview_url && (
                            <div style={{marginBottom: 16}}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Demo site linki"
                                    value={run.preview_url}
                                    InputProps={{readOnly: true}}
                                    onFocus={e => e.target.select()}
                                />
                                <div style={{display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap'}}>
                                    <Button variant="contained" color="primary" onClick={handleCopy} style={buttonStyle}>
                                        {copied ? 'Kopyalandı' : 'Kopyala'}
                                    </Button>
                                    <Button variant="outlined" color="primary" onClick={handleOpenLink} style={buttonStyle}>
                                        Aç
                                    </Button>
                                </div>
                                {run.expires_at && (
                                    <Typography variant="caption" style={{display: 'block', color: '#5b6675', marginTop: 6}}>
                                        Link {formatTime(run.expires_at)} tarihine kadar geçerli.
                                    </Typography>
                                )}
                            </div>
                        )}
                    </>
                )}

                {!run && recentRuns.length > 0 && (
                    <div style={{marginTop: 8}}>
                        <Typography variant="subtitle2" style={{marginBottom: 6}}>Son demolar</Typography>
                        {recentRuns.map(item => (
                            <div
                                key={item.run_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleSelectRun(item)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        handleSelectRun(item);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '6px 0',
                                    borderBottom: '1px solid #eef0f4',
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{color: '#1f2937'}}>{getRunTitle(item)}</span>
                                <span style={{color: '#5b6675'}}>
                                    {RUN_STATUS_TEXT[item.status] || item.status}
                                    {item.created_at && ` · ${formatTime(item.created_at)}`}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                {run && run.property_id && !isActive && (
                    <Button onClick={handleGoToProperty} style={buttonStyle}>Tesise Geç</Button>
                )}
                {run && canRetry && (
                    <Button onClick={handleRetry} disabled={startFetching} style={buttonStyle}>
                        {startFetching ? <CircularProgress size={20} color="inherit"/> : 'Tekrar Dene'}
                    </Button>
                )}
                {run && (
                    <Button onClick={handleNewDemo} style={buttonStyle}>Yeni Demo</Button>
                )}
                <Button onClick={onClose} style={buttonStyle}>Kapat</Button>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = state => ({
    runs: state.salesDemoReducer && state.salesDemoReducer.runs,
    run: state.salesDemoReducer && state.salesDemoReducer.run,
    listFetching: state.salesDemoReducer && state.salesDemoReducer.listFetching,
    startFetching: state.salesDemoReducer && state.salesDemoReducer.startFetching,
    startError: state.salesDemoReducer && state.salesDemoReducer.startError,
    statusError: state.salesDemoReducer && state.salesDemoReducer.statusError,
    searchQuery: state.salesDemoReducer && state.salesDemoReducer.searchQuery,
    searchFetching: state.salesDemoReducer && state.salesDemoReducer.searchFetching,
    searchResults: (state.salesDemoReducer && state.salesDemoReducer.searchResults) || [],
    searchError: state.salesDemoReducer && state.salesDemoReducer.searchError,
});

const mapDispatchToProps = {
    salesDemoList,
    salesDemoStart,
    salesDemoStatus,
    salesDemoSearch,
    clearSalesDemoSearch,
    clearSalesDemo,
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesDemoDialog);
